import type { Earn } from './earn';

/**
 * Mounts and vehicles (สัตว์ขี่ / พาหนะ): ridden out of combat to travel faster. The art reference
 * is docs/art/mounts-ref (14 mounts and vehicles beside the flying sword and the eagles).
 */

/**
 * How a mount travels: on the ground (a horse, or the flying sword skimming over it), in the air
 * (Space climbs, Ctrl (the dash key) dives) or on water (a boat).
 */
export type MountMove = 'ground' | 'fly' | 'water';

export interface MountDef {
  readonly id: string;
  readonly name: string;
  /** Han glyph for the mount button (must be in UI_GLYPHS). */
  readonly glyph: string;
  readonly move: MountMove;
  /** Movement speed multiplier while riding. */
  readonly speedMul: number;
  /** How high the rider is carried above the ground, in world pixels (for drawing). */
  readonly hover: number;
  /**
   * A flying mount (`move: 'fly'`, set from it): Space climbs, Ctrl (the dash key) dives, and high enough it passes
   * over everything.
   */
  readonly fly?: boolean;
  /**
   * Ridden only by the bearer of this title: the title grants it, and a save without the title
   * does not keep it (progress.ts). Never sold or given as a reward (test/mounts.test.ts).
   */
  readonly title?: string;
  /** How it is earned (data/earn). */
  readonly earn: Earn;
  /**
   * The 3D model: `mount-<family>-<variant>`. A pet of the same animal shares the family (one rigged
   * model, scaled and dressed differently; see `modelFamily`).
   */
  readonly modelId: string;
  readonly desc: string;
}

type MountSpec = Omit<MountDef, 'fly'>;

const specs: MountSpec[] = [
  {
    id: 'flying-sword',
    name: 'กระบี่เหาะ',
    glyph: '御',
    move: 'ground',
    earn: { via: 'start' },
    modelId: 'mount-sword-flying',
    speedMul: 1.8,
    hover: 7,
    desc: 'ยืนบนกระบี่ที่ลอยเหนือพื้น ใช้ลมปราณบังคับให้เหาะไปข้างหน้า เคลื่อนที่เร็วขึ้นมาก ใช้ได้เมื่อไม่อยู่ในการต่อสู้',
  },
  {
    id: 'eagle',
    name: 'นกอินทรีทะยานฟ้า',
    glyph: '鷹',
    move: 'fly',
    earn: { via: 'topup', baht: 1500 },
    modelId: 'mount-eagle-brown',
    // No faster than the flying sword: a mount bought with a top-up sells no travel time.
    speedMul: 1.8,
    hover: 14,
    desc: 'นกอินทรีภูเขาตัวใหญ่ที่ฝึกให้รับคนขึ้นหลัง ยืนบนหลังแล้วทะยานขึ้นฟ้า กด Space บินขึ้น กด Ctrl บินลง บินสูงข้ามทุกสิ่งได้',
  },
  {
    id: 'black-eagle',
    name: 'กวางเมฆหิมะ',
    glyph: '麋',
    move: 'fly',
    earn: { via: 'title', title: 'dugu' },
    modelId: 'mount-eagle-black',
    speedMul: 2.2,
    hover: 14,
    title: 'dugu',
    desc: 'กวางขาวเขาดำแห่งยอดเขาหิมะ เหยียบเมฆได้ดุจพื้นดิน ผู้เคยเดินเคียงเถาฉางเยี่ย ยอมให้ผู้ถือฉายานี้ขึ้นหลังเพียงผู้เดียว กด Space บินขึ้น กด Ctrl บินลง บินสูงข้ามทุกสิ่งได้',
  },
  // ── From the mount reference (docs/art/mounts-ref). Bought mounts travel no faster than the
  // flying sword everyone starts with; only legends go faster. ──
  {
    id: 'horse-bay',
    name: 'ม้าน้ำตาลอานชาด',
    glyph: '騮',
    move: 'ground',
    earn: { via: 'shop', gold: 1200 },
    modelId: 'mount-horse-bay',
    speedMul: 1.6,
    hover: 11,
    desc: 'ม้าสีน้ำตาลแดงขนเงา อานชาดขลิบทอง บังเหียนห้อยพู่แดง ม้าคู่ใจของผู้ท่องยุทธภพทุกเส้นทาง พ่อค้าที่ลานเลี้ยงไว้ขายเอง',
  },
  {
    id: 'horse-white',
    name: 'ม้าขาวอานคราม',
    glyph: '驄',
    move: 'ground',
    earn: { via: 'quest', quest: 'black-sword' },
    modelId: 'mount-horse-white',
    speedMul: 1.7,
    hover: 11,
    desc: 'ม้าขาวดุจหิมะ เครื่องม้าสีครามประดับเงิน ผู้เฒ่าหลิวมอบให้ผู้ที่ปราบปีศาจกระบี่ทมิฬ เพื่อออกเดินทางตามหากระบี่เล่มที่สิบต่อไป',
  },
  {
    id: 'horse-black',
    name: 'ม้าดำอานทอง',
    glyph: '驪',
    move: 'ground',
    earn: { via: 'pass', season: 's1', tier: 15, track: 'free' },
    modelId: 'mount-horse-black',
    speedMul: 1.7,
    hover: 11,
    desc: 'ม้าดำสนิททั้งตัว อานแดงขลิบทอง วิ่งกลางคืนแทบมองไม่เห็น มอบแก่ผู้ที่เดินทางมาครึ่งฤดูกาล',
  },
  {
    id: 'horse-fire',
    name: 'ม้าเพลิง',
    glyph: '焰',
    move: 'ground',
    earn: { via: 'mall', jade: 1280 },
    modelId: 'mount-horse-fire',
    speedMul: 1.8,
    hover: 11,
    desc: 'ม้าดำที่แผงคอ หาง และกีบลุกเป็นเปลวไฟ เหยียบที่ใดหญ้าไหม้เป็นรอยเท้า ไฟของมันไม่ลวกผู้ขี่ที่มันยอมรับ',
  },
  {
    id: 'wolf-white',
    name: 'หมาป่าหิมะ',
    glyph: '狼',
    move: 'ground',
    earn: { via: 'pass', season: 's1', tier: 15, track: 'premium' },
    modelId: 'mount-wolf-white',
    speedMul: 1.7,
    hover: 9,
    desc: 'หมาป่าขาวตัวใหญ่สวมอานเกราะสีคราม วิ่งบนหิมะไม่จมเท้า ได้กลิ่นศัตรูก่อนผู้ขี่จะเห็นเสมอ',
  },
  {
    id: 'tiger',
    name: 'เสือลายทอง',
    glyph: '虎',
    move: 'ground',
    earn: { via: 'mall', jade: 980 },
    modelId: 'mount-tiger-gold',
    speedMul: 1.75,
    hover: 9,
    desc: 'เสือโคร่งขนส้มลายดำ อานสีครามรัดแน่น ย่องเงียบกว่าม้าทุกตัว แต่เมื่อคำรามสัตว์ป่าทั้งเขาหลบทาง',
  },
  {
    id: 'stag',
    name: 'กวางเขาพันกิ่ง',
    glyph: '鹿',
    move: 'ground',
    earn: { via: 'profession', profession: 'herbalist', level: 8 },
    modelId: 'mount-stag-white',
    speedMul: 1.75,
    hover: 11,
    desc: 'กวางขาวเขาแตกกิ่งดุจต้นไม้ อานบางเบา มันตามกลิ่นหลินจือมาหาหมอยาที่เก็บสมุนไพรโดยไม่ถอนราก แล้วไม่ยอมจากไปอีก',
  },
  {
    id: 'ox',
    name: 'กระทิงเกราะชาด',
    glyph: '牛',
    move: 'ground',
    earn: { via: 'profession', profession: 'farmer', level: 6 },
    modelId: 'mount-ox-wild',
    speedMul: 1.5,
    hover: 10,
    desc: 'กระทิงป่าเขาโค้งใหญ่ สวมอานเกราะสีชาด ช้ากว่าม้าแต่ไม่มีวันเหนื่อย ชาวนาที่ไถนามาทั้งชีวิตเท่านั้นที่มันยอมให้จูง',
  },
  {
    id: 'panda',
    name: 'แพนด้าไผ่หยก',
    glyph: '熊',
    move: 'ground',
    earn: { via: 'mall', jade: 680 },
    modelId: 'mount-panda-jade',
    speedMul: 1.5,
    hover: 9,
    desc: 'แพนด้าอ้วนกลม อานสีเขียวขลิบทองมีกิ่งไผ่ปักไว้ให้เคี้ยวระหว่างทาง เดินอุ้ยอ้ายแต่ใครเห็นก็ยิ้ม',
  },
  {
    id: 'crane',
    name: 'กระเรียนมงกุฎแดง',
    glyph: '鶴',
    move: 'fly',
    earn: { via: 'pass', season: 's1', tier: 25, track: 'premium' },
    modelId: 'mount-crane-red',
    speedMul: 1.8,
    hover: 14,
    desc: 'กระเรียนขาวหัวแดงพาหนะของเซียนในภาพวาด กางปีกแล้วร่อนขึ้นฟ้าอย่างสง่างาม กด Space บินขึ้น กด Ctrl บินลง บินสูงข้ามทุกสิ่งได้',
  },
  {
    id: 'dragon',
    name: 'มังกรคราม',
    glyph: '龍',
    move: 'fly',
    earn: {
      via: 'legend',
      how: 'ตำนาน: ผู้ได้ฉายาตำนานเก้ากระบี่ และปราบจอมมารพันกระบี่ได้สิบครั้ง',
    },
    modelId: 'mount-dragon-azure',
    speedMul: 2.2,
    hover: 16,
    desc: 'มังกรเกล็ดครามลำตัวยาวดุจแม่น้ำ ขดตัวรอบเมฆแล้วพาผู้ขี่ทะยานขึ้นฟ้า ทั้งยุทธภพมีผู้เคยขี่ได้ไม่ถึงนิ้วมือ กด Space บินขึ้น กด Ctrl บินลง',
  },
  {
    id: 'qilin',
    name: 'กิเลนเมฆขาว',
    glyph: '麟',
    move: 'fly',
    earn: {
      via: 'legend',
      how: 'ตำนาน: ฝึกกำลังภายในเล่มใดก็ได้ขั้นสองจนตัน แล้วไหว้ศิลาเก้ากระบี่ในคืนจันทร์เต็มดวง',
    },
    modelId: 'mount-qilin-white',
    speedMul: 2.2,
    hover: 13,
    desc: 'กิเลนขาวเขาทอง กีบเหยียบเมฆแทนดิน สัตว์มงคลที่ปรากฏเมื่อยุทธภพจะมีผู้ยิ่งใหญ่ เดินบนฟ้าได้ดุจเดินบนพื้น กด Space บินขึ้น กด Ctrl บินลง',
  },
  {
    id: 'carriage',
    name: 'รถม้าชาด',
    glyph: '車',
    move: 'ground',
    earn: { via: 'hire' },
    modelId: 'mount-carriage-lacquer',
    speedMul: 1.4,
    hover: 8,
    desc: 'รถม้าลงรักสีชาดหลังคาทอง ม้าน้ำตาลหนึ่งตัวลาก นั่งได้ทั้งคณะ เช่าได้ที่ท่ารถม้าทุกเมืองและทุกสำนัก',
  },
  {
    id: 'boat',
    name: 'เรือประทุนโคมแดง',
    glyph: '舟',
    move: 'water',
    earn: { via: 'profession', profession: 'fisher', level: 10 },
    modelId: 'mount-boat-lantern',
    speedMul: 1.5,
    hover: 4,
    desc: 'เรือไม้หลังคาประทุน แขวนโคมแดงรอบลำ ล่องไปตามแม่น้ำและทะเลสาบ ชาวประมงผู้ชำนาญต่อขึ้นเองจากไม้สนเก่า',
  },
  {
    id: 'bamboo-log',
    name: 'ไผ่ลำเดียว',
    glyph: '筏',
    move: 'water',
    earn: { via: 'legend', how: 'เควสต์เนื้อเรื่องทุกเส้น: ผู้พายเรือแจกไม้ไผ่ 1 ท่อน ให้ยืนพายข้ามน้ำ' },
    // Placeholder model id until the bamboo log is rigged (the client lane does the standing-and-poling pose).
    modelId: 'mount-bamboo-log',
    speedMul: 0.9,
    hover: 3,
    desc: 'ไม้ไผ่ลำเดียวมัดเป็นแพเรียวยาว ผู้ขี่ยืนบนลำแล้วใช้ไม้ไผ่ยาวปักพื้นน้ำพายไป ช้ากว่าเรือแต่พาข้ามลำธารได้ทุกสาย', // TODO-polish
  },
  {
    id: 'firebird',
    name: 'นกเพลิงปี้ฟาง',
    glyph: '鳳',
    move: 'fly',
    earn: { via: 'boss', boss: 'wb-firebird', how: 'หีบบอสโลก: นกเพลิงปี้ฟาง (โอกาสสุ่ม 0.5%)' },
    // Placeholder model id until the firebird is rigged (docs/handoff/newdir/ITEMS-V2.md).
    modelId: 'mount-firebird-red',
    speedMul: 1.8,
    hover: 14,
    desc: 'นกเพลิงตัวโตขนแดงทองปลายเป็นเปลวไฟ ไม่ลวกผู้ขี่ บินได้สูงเท่านกอินทรี กด Space บินขึ้น กด Ctrl บินลง',
  },
];

const list: MountDef[] = specs.map((m) => (m.move === 'fly' ? { ...m, fly: true } : m));

export const MOUNTS: Readonly<Record<string, MountDef>> = Object.fromEntries(list.map((m) => [m.id, m]));

export function getMount(id: string): MountDef {
  const m = MOUNTS[id];
  if (!m) throw new Error(`Unknown mount: ${id}`);
  return m;
}

/** Mounts every character owns from the start. */
export const STARTER_MOUNTS = ['flying-sword'];

export const MOUNT_GLYPHS = list.map((m) => m.glyph).join('');
export const MOUNT_LIST: readonly MountDef[] = list;

/** A model id's family, the animal or vehicle one rigged model is made for (`mount-horse-bay` → `horse`). */
export const modelFamily = (modelId: string): string => modelId.split('-')[1] ?? '';

/** A model id: `mount-` or `pet-`, the family, then an optional variant, in lower-case kebab. */
export const MODEL_ID = /^(mount|pet)-[a-z]+(-[a-z0-9]+)*$/;
